import type { AgentTurnSchema } from '@avisoma/shared';
import { Badge } from '../../catalyst/badge';
import { DescriptionDetails, DescriptionList, DescriptionTerm } from '../../catalyst/description-list';
import { formatNumber } from '../../lib/numberFormat';

type AgentTurn = AgentTurnSchema<string, string>;

const blockText = 'whitespace-pre-wrap break-words';
const muted = 'text-gray-500 dark:text-zinc-400';

function formatDuration(ms: number): string {
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

export default function TurnDetails({ turn }: { turn: AgentTurn }) {
  return (
    <DescriptionList className="sm:!grid-cols-[11rem_auto] whitespace-normal">
      <DescriptionTerm>Session question</DescriptionTerm>
      <DescriptionDetails className={blockText}>{turn.sessionQuestion}</DescriptionDetails>

      <DescriptionTerm>Question</DescriptionTerm>
      <DescriptionDetails className={blockText}>{turn.question}</DescriptionDetails>

      <DescriptionTerm>Answer excerpt</DescriptionTerm>
      <DescriptionDetails className={blockText}>
        {turn.answerExcerpt ?? <span className={muted}>No answer text streamed</span>}
      </DescriptionDetails>

      <DescriptionTerm>Gap</DescriptionTerm>
      <DescriptionDetails className={blockText}>{turn.gap ?? <span className={muted}>—</span>}</DescriptionDetails>

      <DescriptionTerm>Tools</DescriptionTerm>
      <DescriptionDetails>
        {turn.toolCalls.length > 0 ? (
          <span className="flex flex-wrap gap-1">
            {turn.toolCalls.map((tool, index) => (
              <Badge key={index} color="zinc" className="font-mono">
                {tool}
              </Badge>
            ))}
          </span>
        ) : (
          <span className={muted}>No tool calls</span>
        )}
      </DescriptionDetails>

      <DescriptionTerm>Empty tool results</DescriptionTerm>
      <DescriptionDetails>
        {turn.emptyToolResults} of {turn.toolCalls.length}
      </DescriptionDetails>

      <DescriptionTerm>Steps</DescriptionTerm>
      <DescriptionDetails>{turn.steps}</DescriptionDetails>

      <DescriptionTerm>Tokens</DescriptionTerm>
      <DescriptionDetails>
        {formatNumber(turn.inputTokens)} in / {formatNumber(turn.outputTokens)} out
      </DescriptionDetails>

      <DescriptionTerm>Duration</DescriptionTerm>
      <DescriptionDetails>{formatDuration(turn.durationMs)}</DescriptionDetails>

      <DescriptionTerm>Partial reason</DescriptionTerm>
      <DescriptionDetails className="font-mono">
        {turn.partialReason ?? <span className={`font-sans ${muted}`}>—</span>}
      </DescriptionDetails>

      <DescriptionTerm>Error</DescriptionTerm>
      <DescriptionDetails className={blockText}>
        {turn.error ? (
          <span className="text-red-600 dark:text-red-400">{turn.error}</span>
        ) : (
          <span className={muted}>—</span>
        )}
      </DescriptionDetails>
    </DescriptionList>
  );
}
